import { Skeleton, Box, Heading } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetDailyConcentrationInsightsQuery } from 'packages/portfolio/requests/requests.gql.generated';
import { generateConcentrationLimitChartConfig } from 'packages/portfolio/ui/borrowingBase/portfolioBorrowingBaseChartConfigs';
import { PortfolioConcentrationLimitsChartTemplate } from 'packages/portfolio/ui/borrowingBase/PortfolioConcentrationLimitsChartTemplate';
import { PortfolioExcessConcentrationChart } from 'packages/portfolio/ui/borrowingBase/PortfolioExcessConcentrationChart';
import {
  ConcentrationLimitFormatMap,
  getConcentrationLimitFormatsByKey,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';

export const ConcentrationContainerV2 = () => {
  const { creditFacilityId } = useCreditFacilityContext();

  const {
    data: concentrationData,
    loading: isConcentrationDataLoading,
  } = useGetDailyConcentrationInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
  });

  const concentrationLimitFormatMap = useMemo<ConcentrationLimitFormatMap>(() => {
    if (!concentrationData) {
      return new Map();
    }
    return getConcentrationLimitFormatsByKey(
      concentrationData.getDailyConcentrationInsights,
    );
  }, [concentrationData]);

  if (!concentrationData || isConcentrationDataLoading) {
    return <Skeleton data-testid="loading-insights-view" height="600px" />;
  }

  return (
    <Box height="100%">
      <PortfolioExcessConcentrationChart
        creditFacilityId={creditFacilityId}
        chartTitle="Excess Concentration Amounts"
        concentrationLimitFormatMap={concentrationLimitFormatMap}
      />
      {concentrationLimitFormatMap.size > 0 && (
        <>
          <Heading as="h3" fontSize="24px" mb="24px">
            Concentration Limits
          </Heading>
          <HighlightBox backgroundColor={colors.millet[50]}>
            {Array.from(concentrationLimitFormatMap).map(
              ([concentrationLimitKey, valueType]) => (
                <PortfolioConcentrationLimitsChartTemplate
                  creditFacilityId={creditFacilityId}
                  chartTitle={commonUtils.toTitleCase(concentrationLimitKey)}
                  chartConfig={generateConcentrationLimitChartConfig(
                    valueType === 'number' ? 'float' : valueType,
                  )}
                  concentrationLimitKey={concentrationLimitKey}
                  key={`portfolio-concentration-limit-chart-${concentrationLimitKey}`}
                />
              ),
            )}
          </HighlightBox>
        </>
      )}
    </Box>
  );
};
